<template>
  <v-card flat rounded="lg" class="pa-4 summary-chart-card">
    <Line
      v-if="chartData.datasets.length"
      :key="chartRenderKey"
      :data="chartData"
      :options="chartOptions"
    />
    <v-alert v-else type="info" class="mt-4">No data available for chart.</v-alert>
  </v-card>
</template>

<script setup>
import { computed } from 'vue'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BubbleController,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'

import { Line } from 'vue-chartjs'

// Register Chart.js components. BubbleController is required because the
// Buy/Sell/platform-avg markers are pushed in as `type: 'bubble'` datasets
// alongside the line datasets on the same chart.
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BubbleController, Title, Tooltip, Legend, Filler)

const props = defineProps({
  rawData: {
    type: Object,
    default: () => ({
      labels: [],
      datasets: []
    })
  },
  chartOptions: {
    type: Object,
    default: () => ({
      responsive: true,
      plugins: {
        legend: { display: true },
        title: {
          display: true,
          text: 'Stock Summary Over Time'
        },
        tooltip: {
          mode: 'index',
          intersect: false
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      },
      scales: {
        x: {
          ticks: {
            autoSkip: true,
            maxTicksLimit: 10,
            minRotation: 0,
            maxRotation: 45
          }
        },
        y: {
          beginAtZero: true
        }
      }
    })
  }
})

const chartData = computed(() => ({
  labels: Array.isArray(props.rawData?.labels) ? [...props.rawData.labels] : [],
  datasets: Array.isArray(props.rawData?.datasets)
    ? props.rawData.datasets.map((dataset) => ({
        ...dataset,
        data: Array.isArray(dataset?.data)
          ? dataset.data.map((point) =>
              point && typeof point === 'object' ? { ...point } : point
            )
          : []
      }))
    : []
}))

const chartRenderKey = computed(() =>
  JSON.stringify({
    labels: chartData.value.labels,
    datasets: chartData.value.datasets.map((dataset) => ({
      label: dataset.label,
      data: dataset.data
    }))
  })
)
</script>

<style scoped>
.summary-chart-card {
  border: 1px solid var(--border);
}
</style>
